/**
 * What a table looks like, stored ON the table.
 *
 * EVERY ONE OF THESE HAS TO REACH THE RENDITION. A border weight, a cell padding or a column width
 * that lived only in this stylesheet would be missing from the PDF people sign -- so each is an
 * attribute of the document, written into the element's style on the way out and read back off it
 * on the way in, which is also what makes a table pasted out of this editor keep its shape.
 *
 * CASCADED WITH CSS CUSTOM PROPERTIES. A border and a padding belong to the CELLS, but the author
 * sets them on the TABLE; writing them onto every cell would mean a transaction over the whole
 * table for one spinner and a document that re-renders differently if a cell was added afterwards.
 * Custom properties inherit, so one declaration on the table reaches every cell in it, now and
 * later -- and the rendition reads the same single attribute.
 */
import { Extension } from "@tiptap/core";
import { NodeSelection, Plugin, PluginKey } from "@tiptap/pm/state";
import { Decoration, DecorationSet } from "@tiptap/pm/view";
import { declaredTypes, padded, violation, withTypeAt } from "./columnTypes";
import { sortedByKey } from "./rowSort";
import type { SortDirection } from "./rowSort";
import type { ColumnType } from "./columnTypes";

const number = (value: any) => {
    const parsed = parseFloat(value);
    return Number.isFinite(parsed) ? parsed : null;
};

/** The look of a table, as the panel sets it. Nulls mean "the document's default". */
export type TableLook = {
    align: "left" | "center" | "right" | null;
    indent: number | null;              // inches
    borderWidth: number | null;         // points; 0 means a borderless layout table
    borderColor: string | null;
    cellPadding: number | null;         // points
    overflowRows: boolean;              // may a row break across a page?
};

export const DEFAULT_LOOK: TableLook = {
    align: "left", indent: 0, borderWidth: 0.75, borderColor: "#9AA0A6",
    cellPadding: 4, overflowRows: true,
};

export const TableAttributes = Extension.create({
    name: "tableAttributes",

    addGlobalAttributes() {
        return [
            {
                // A MINIMUM HEIGHT IS THE ROW'S, not the table's and not the cell's. It is how a
                // signature block is drawn in every procedure anybody has signed: a row tall
                // enough to write in, whatever is typed in it. The renderer honours it by taking
                // the LARGER of this and what the row's content needs, so it is a floor rather
                // than a fixed height -- a row that is given more to say still grows.
                types: ["tableRow"],
                attributes: {
                    minHeight: {
                        default: null,
                        parseHTML: (element: HTMLElement) =>
                            number(element.getAttribute("data-min-height")),
                        // DRAWN ON THE ROW, where a browser treats `height` on a table row as a
                        // minimum rather than as a size -- which is exactly the meaning wanted,
                        // and is why this is not `min-height` (which table rows ignore).
                        renderHTML: (attrs: any) => attrs.minHeight
                            ? { "data-min-height": attrs.minHeight,
                                style: `height:${attrs.minHeight}in` }
                            : {},
                    },
                },
            },
            {
                types: ["table"],
                attributes: {
                    align: {
                        default: null,
                        parseHTML: (element) => element.getAttribute("data-align"),
                        renderHTML: (attrs) => (!attrs.align || attrs.align === "left") ? {} : {
                            "data-align": attrs.align,
                            style: attrs.align === "center"
                                ? "margin-left:auto;margin-right:auto"
                                : "margin-left:auto;margin-right:0",
                        },
                    },
                    indent: {
                        default: null,
                        parseHTML: (element) => number(element.getAttribute("data-indent")),
                        renderHTML: (attrs) => attrs.indent
                            ? { "data-indent": attrs.indent, style: `margin-left:${attrs.indent}in` }
                            : {},
                    },
                    // THE THREE THAT CASCADE. Written as custom properties so one declaration on the
                    // table reaches every cell -- including cells added after the setting was made.
                    borderWidth: {
                        default: null,
                        parseHTML: (element) => number(element.getAttribute("data-border-width")),
                        renderHTML: (attrs) => attrs.borderWidth === null || attrs.borderWidth === undefined
                            ? {}
                            : { "data-border-width": attrs.borderWidth,
                                style: `--parkour-table-border-width:${attrs.borderWidth}pt` },
                    },
                    borderColor: {
                        default: null,
                        parseHTML: (element) => element.getAttribute("data-border-color"),
                        renderHTML: (attrs) => attrs.borderColor
                            ? { "data-border-color": attrs.borderColor,
                                style: `--parkour-table-border-color:${attrs.borderColor}` }
                            : {},
                    },
                    cellPadding: {
                        default: null,
                        parseHTML: (element) => number(element.getAttribute("data-cell-padding")),
                        renderHTML: (attrs) => attrs.cellPadding === null || attrs.cellPadding === undefined
                            ? {}
                            : { "data-cell-padding": attrs.cellPadding,
                                style: `--parkour-table-cell-padding:${attrs.cellPadding}pt` },
                    },
                    // WHAT MAY BE WRITTEN IN EACH COLUMN -- an array indexed by column, nulls for
                    // the ones with no constraint. See columnTypes.ts for why this lives on the
                    // TABLE rather than on each cell the way `colwidth` does: a new row's cells are
                    // built with default attributes, so a constraint kept per cell would vanish for
                    // every row added after it was set.
                    //
                    // JSON IN AN ATTRIBUTE, so it survives a copy and paste out of this editor and
                    // back in. A table pasted elsewhere keeps the rule it was given.
                    columnTypes: {
                        default: null,
                        parseHTML: (element: HTMLElement) => {
                            try {
                                const raw = element.getAttribute("data-column-types");
                                const parsed = raw ? JSON.parse(raw) : null;
                                return Array.isArray(parsed) ? parsed : null;
                            } catch {
                                return null;   // a table from somewhere else, with no rule on it
                            }
                        },
                        renderHTML: (attrs: any) =>
                            Array.isArray(attrs.columnTypes) && attrs.columnTypes.some(Boolean)
                                ? { "data-column-types": JSON.stringify(attrs.columnTypes) }
                                : {},
                    },
                    // NOT A LOOK AT ALL, and that is why it is here rather than in a stylesheet: it
                    // decides whether a row may be split by a page break, which only the thing that
                    // paginates can honour. The editor's preview and the rendition both read it.
                    overflowRows: {
                        default: null,
                        parseHTML: (element) => element.getAttribute("data-overflow-rows") !== "false",
                        renderHTML: (attrs) => attrs.overflowRows === false
                            ? { "data-overflow-rows": "false" } : {},
                    },
                },
            },
            {
                types: ["tableCell", "tableHeader"],
                attributes: {
                    verticalAlign: {
                        default: null,
                        parseHTML: (element) => element.style.verticalAlign || null,
                        renderHTML: (attrs) => attrs.verticalAlign
                            ? { style: `vertical-align:${attrs.verticalAlign}` } : {},
                    },
                    backgroundColor: {
                        default: null,
                        parseHTML: (element) => element.style.backgroundColor || null,
                        renderHTML: (attrs) => attrs.backgroundColor
                            ? { style: `background-color:${attrs.backgroundColor}` } : {},
                    },
                },
            },
        ];
    },
});

/** The table the caret is in, as {pos, node, depth}, or null. */
export function tableAt(editor: any): { pos: number; node: any; depth: number } | null {
    const { $from } = editor?.state?.selection ?? {};
    if (!$from) return null;
    for (let depth = $from.depth; depth > 0; depth -= 1) {
        const node = $from.node(depth);
        if (node.type.name === "table") return { pos: $from.before(depth), node, depth };
    }
    return null;
}

/** What the panel shows: the table's own settings, with the document's defaults filled in. */
export function tableLook(editor: any): TableLook | null {
    const table = tableAt(editor);
    if (!table) return null;
    const attrs = table.node.attrs || {};
    return {
        align: attrs.align ?? DEFAULT_LOOK.align,
        indent: attrs.indent ?? DEFAULT_LOOK.indent,
        borderWidth: attrs.borderWidth ?? DEFAULT_LOOK.borderWidth,
        borderColor: attrs.borderColor ?? DEFAULT_LOOK.borderColor,
        cellPadding: attrs.cellPadding ?? DEFAULT_LOOK.cellPadding,
        overflowRows: attrs.overflowRows !== false,
    };
}

export function setTableLook(editor: any, next: Partial<TableLook>) {
    const table = tableAt(editor);
    if (!editor || !table) return;
    const { state, view } = editor;
    view.dispatch(state.tr.setNodeMarkup(table.pos, undefined, { ...table.node.attrs, ...next }));
    view.focus();
}

/** Whether the caret's table has a header row -- the thing "Pin header row(s)" turns on. */
export function tableHasHeader(editor: any): boolean {
    const first = tableAt(editor)?.node.firstChild;
    if (!first) return false;
    let header = false;
    first.forEach((cell: any) => { if (cell.type.name === "tableHeader") header = true; });
    return header;
}

/** The cell the caret is in, for the per-cell controls. */
function cellAt(editor: any): { pos: number; node: any } | null {
    const { $from } = editor?.state?.selection ?? {};
    if (!$from) return null;
    for (let depth = $from.depth; depth > 0; depth -= 1) {
        const node = $from.node(depth);
        if (node.type.name === "tableCell" || node.type.name === "tableHeader") {
            return { pos: $from.before(depth), node };
        }
    }
    return null;
}

export const cellAttribute = (editor: any, name: string) => cellAt(editor)?.node.attrs?.[name] ?? null;

/** The row the caret is in, as a position and a node. */
export function rowAt(editor: any): { pos: number; node: any } | null {
    const selection = editor?.state?.selection;
    if (!selection) return null;
    for (let depth = selection.$from.depth; depth > 0; depth -= 1) {
        const node = selection.$from.node(depth);
        if (node.type.name === "tableRow") return { pos: selection.$from.before(depth), node };
    }
    return null;
}

/** What this row says its minimum height is, in inches, or null. */
export const rowMinHeight = (editor: any) => rowAt(editor)?.node?.attrs?.minHeight ?? null;

/**
 * Set it, in inches, or clear it with null.
 *
 * A FLOOR, NOT A HEIGHT, at both ends: the browser treats `height` on a row as a minimum, and the
 * renderer takes the larger of this and what the content needs. A row that is given more to say
 * still grows, which is what stops this from being a way to lose text.
 */
export function setRowMinHeight(editor: any, inches: number | null) {
    const row = rowAt(editor);
    if (!editor || !row) return;
    const { state, view } = editor;
    const clean = inches === null || !Number.isFinite(inches) || inches <= 0
        ? null : Math.min(9, Math.max(0.05, inches));
    view.dispatch(state.tr.setNodeMarkup(row.pos, undefined,
                                         { ...row.node.attrs, minHeight: clean }));
    view.focus();
}

export function setCellAttribute(editor: any, name: string, value: any) {
    if (!editor) return;
    // setCellAttribute acts on EVERY cell in the selection, which is what a person who has dragged
    // across three cells means by "pad these". With a plain caret it is the one cell they are in.
    editor.chain().focus().setCellAttribute(name, value).run();
}

/**
 * Column width, in inches, for the column the caret is in.
 *
 * WRITTEN ONTO EVERY CELL OF THE COLUMN, because that is where ProseMirror's table schema keeps it
 * (`colwidth`) and where the colgroup is built from. A width on the table alone would be a number
 * nothing reads.
 */
export function columnWidth(editor: any): number | null {
    const cell = cellAt(editor);
    const width = cell?.node.attrs?.colwidth;
    const pixels = Array.isArray(width) ? width[0] : width;
    return pixels ? Math.round((pixels / 96) * 1000) / 1000 : null;
}

export function setColumnWidth(editor: any, inches: number | null) {
    if (!editor) return;
    const pixels = inches ? Math.round(inches * 96) : null;
    editor.chain().focus().setCellAttribute("colwidth", pixels ? [pixels] : null).run();
}


/**
 * Quick layouts: a width and a position, in one click.
 *
 * NOT GOOGLE'S NINE, and the difference is worth stating rather than hiding. Google's quick layouts
 * are TEXT-WRAP positions -- the table sitting in the prose with paragraphs flowing beside it.
 * Platypus, which draws the signed PDF, has no wrap-around-a-table at all, so those nine could be
 * made to look right in the editor and could never be made to print. A control that is correct on
 * screen and wrong on paper is the one thing this feature exists to prevent.
 *
 * So the grid is the same 3x3 affordance with the two choices that DO reach the rendition: how wide
 * the table is, and where it sits. Both are already read by the renderer -- column widths and
 * alignment -- so a quick layout is nothing new to render, just a faster way to say it.
 */
export const LAYOUT_WIDTHS: Array<[string, number]> = [
    ["Full width", 1], ["Two thirds", 2 / 3], ["Half", 0.5],
];
export const LAYOUT_ALIGNS: Array<"left" | "center" | "right"> = ["left", "center", "right"];

/** The text column a table lives in: US Letter less two inches of margin. */
const TEXT_COLUMN_INCHES = 6.5;

/**
 * Set every column of the table to an equal share of `fraction` of the text column, and place it.
 *
 * EQUAL SHARES, deliberately: a quick layout is for getting back to something sane in one click,
 * and the per-column control next to it is for the cases where the columns are not equal. Written
 * onto every cell because that is where ProseMirror's table schema keeps a width.
 */
export function applyQuickLayout(editor: any, fraction: number, align: "left" | "center" | "right") {
    const table = tableAt(editor);
    if (!editor || !table) return;

    const first = table.node.firstChild;
    if (!first) return;
    let columns = 0;
    first.forEach((cell: any) => { columns += cell.attrs?.colspan || 1; });
    if (!columns) return;

    const pixels = Math.max(24, Math.round((TEXT_COLUMN_INCHES * fraction * 96) / columns));

    const { state, view } = editor;
    const tr = state.tr;
    table.node.descendants((node: any, pos: number) => {
        if (node.type.name !== "tableCell" && node.type.name !== "tableHeader") return true;
        // +1 for the table node's own opening token: descendants() reports positions relative to
        // the node's content, and setNodeMarkup wants them in the document.
        tr.setNodeMarkup(table.pos + 1 + pos, undefined, { ...node.attrs, colwidth: [pixels] });
        return false;
    });
    // THE TABLE'S OWN ATTRIBUTES LAST, mapped through the cell changes above -- every one of them
    // shifted nothing, but reading the position back through the mapping is what keeps this correct
    // the day one of them does.
    const at = tr.mapping.map(table.pos);
    const node = tr.doc.nodeAt(at);
    if (node) tr.setNodeMarkup(at, undefined, { ...node.attrs, align, indent: 0 });

    view.dispatch(tr);
    view.focus();
}


/**
 * The gripper: a handle at the top-left of every table, as every word processor has.
 *
 * A TABLE HAS NO HANDLE OF ITS OWN. Clicking inside it puts a caret in a cell; there is nothing to
 * take hold of to select or move the whole thing, which is why Google draws one. This is the same
 * handle, as a ProseMirror widget decoration -- so it is drawn by the editor, belongs to no node
 * view, and never enters the document.
 *
 * CLICKING IT SELECTS THE WHOLE TABLE. Not as a NodeSelection, which is what one would reach for
 * and which cannot survive: prosemirror-tables NORMALISES a node selection on a table into a cell
 * selection covering it, deliberately, because that is what its own machinery acts on. So the
 * gripper does what the plugin would have done anyway and selects every cell -- which is also how
 * Google draws a selected table.
 *
 * AND DRAGGING IT MOVES THE TABLE, which had to be written rather than inherited for the same
 * reason: ProseMirror moves a node by dragging a NODE selection, and there is never one here. The
 * handle carries the table's position in the drag, and the drop handler below takes the table out
 * and puts it back at the nearest block boundary to where it was let go.
 */
/**
 * Take a table out and put it back where the pointer let go.
 *
 * AT THE NEAREST TOP-LEVEL BOUNDARY, not at the exact character under the pointer: a table cannot
 * go inside a paragraph, and "in the middle of a sentence" is not a place to put one. The block the
 * pointer was over is what it goes before.
 */
function moveTable(view: any, from: number, clientX: number, clientY: number) {
    const node = view.state.doc.nodeAt(from);
    if (!node || node.type.name !== "table") return;

    const dropped = view.posAtCoords({ left: clientX, top: clientY });
    if (!dropped) return;
    // DROPPED ON ITSELF IS A NO-OP, not a delete and re-insert that loses the selection.
    if (dropped.pos > from && dropped.pos < from + node.nodeSize) return;

    const tr = view.state.tr.delete(from, from + node.nodeSize);
    const at = Math.min(tr.mapping.map(dropped.pos), tr.doc.content.size);
    const $at = tr.doc.resolve(at);
    tr.insert($at.depth > 0 ? $at.before(1) : at, node);
    view.dispatch(tr.scrollIntoView());
    view.focus();
}

export const TableGripper = Extension.create({
    name: "tableGripper",

    addProseMirrorPlugins() {
        return [new Plugin({
            key: new PluginKey("parkourTableGripper"),
            props: {
                decorations(state) {
                    const found: Decoration[] = [];
                    state.doc.descendants((node, pos) => {
                        if (node.type.name !== "table") return true;
                        found.push(Decoration.widget(pos, (view) => {
                            const anchor = document.createElement("span");
                            anchor.className = "parkour-table-gripper-anchor";
                            const grip = document.createElement("span");
                            grip.className = "parkour-table-gripper";
                            grip.setAttribute("role", "button");
                            grip.setAttribute("aria-label", "Select this table");
                            grip.setAttribute("contenteditable", "false");

                            // MOUSEDOWN, NOT CLICK. By the time a click fires the editor has already
                            // put the caret somewhere, and the selection we make would be replaced.
                            grip.addEventListener("mousedown", (event) => {
                                // STOPPED HERE, NOT JUST PREVENTED. prosemirror-tables has its own
                                // mousedown handler on the editor's root, and it answers a press
                                // near a table by starting a CELL selection -- which is what came
                                // out of the gripper the first time: a cell selected in whichever
                                // table the pointer was over, not the table taken as a whole.
                                // preventDefault only stops the browser's default; the other
                                // handler still runs unless the event stops bubbling here.
                                event.preventDefault();
                                event.stopPropagation();
                                // AFTER THE PRESS HAS FINISHED BEING A PRESS. A mousedown is
                                // followed by a mouseup and a click, and ProseMirror re-reads the
                                // browser's own selection on the way through -- which is still a
                                // caret in a cell, so a node selection made during the mousedown
                                // was replaced by a cell one before anything could see it. Setting
                                // it on the next tick lands after all of that.
                                // THE MOVE, DRIVEN BY THE POINTER rather than by HTML5 drag.
                                //
                                // The obvious route -- draggable=true, a position in the
                                // dataTransfer, a handleDrop -- does not survive ProseMirror: the
                                // editor has its own dragstart handler on the same element tree and
                                // builds its own drag out of the current selection, and what
                                // arrives at the drop is its slice rather than our table. Watching
                                // the pointer needs no cooperation from anybody.
                                const start = { x: event.clientX, y: event.clientY };
                                let dragging = false;

                                const onMove = (move: MouseEvent) => {
                                    if (!dragging
                                        && Math.abs(move.clientX - start.x) < 4
                                        && Math.abs(move.clientY - start.y) < 4) return;
                                    dragging = true;
                                    document.body.style.cursor = "grabbing";
                                };

                                const onUp = (up: MouseEvent) => {
                                    document.removeEventListener("mousemove", onMove, true);
                                    document.removeEventListener("mouseup", onUp, true);
                                    document.body.style.cursor = "";
                                    if (!dragging) { select(); return; }
                                    moveTable(view, pos, up.clientX, up.clientY);
                                };

                                document.addEventListener("mousemove", onMove, true);
                                document.addEventListener("mouseup", onUp, true);

                                const select = () => {
                                    try {
                                        view.focus();
                                        // A NODE SELECTION IS THE REQUEST; the tableEditing plugin
                                        // turns it into a selection of every cell, which is what
                                        // "the whole table is selected" means to everything else.
                                        view.dispatch(view.state.tr
                                            .setSelection(NodeSelection.create(view.state.doc, pos))
                                            .scrollIntoView());
                                    } catch { /* the table moved under us; the next render redraws */ }
                                };
                                // A PRESS THAT NEVER MOVED IS A CLICK, and selects. The
                                // mouseup above decides which this was.
                            });
                            anchor.appendChild(grip);
                            return anchor;
                        }, { side: -1, ignoreSelection: true }));
                        return false;                 // a table inside a table gets its own on the way
                    });
                    return DecorationSet.create(state.doc, found);
                },

            },
        })];
    },
});


/**
 * Which column the caret is in, counting the real grid rather than the row's own order.
 *
 * A CELL UNDER A ROWSPAN DOES NOT START WHERE ITS POSITION IN THE ROW SUGGESTS, so counting along
 * the row would set a type on the wrong column -- and the author would watch the dropdown they
 * just used appear over a different column. The same walk the server does in column_types.py.
 */
export function columnIndex(editor: any): number | null {
    const table = tableAt(editor);
    const cell = cellAt(editor);
    if (!table || !cell) return null;

    const occupied = new Set<string>();
    let row = -1;
    let found: number | null = null;

    table.node.forEach((rowNode: any, rowOffset: number) => {
        row += 1;
        let column = 0;
        rowNode.forEach((cellNode: any, cellOffset: number) => {
            while (occupied.has(`${row}:${column}`)) column += 1;
            const colspan = cellNode.attrs?.colspan || 1;
            const rowspan = cellNode.attrs?.rowspan || 1;
            for (let dr = 0; dr < rowspan; dr += 1) {
                for (let dc = 0; dc < colspan; dc += 1) occupied.add(`${row + dr}:${column + dc}`);
            }
            // +1 for the table's own opening token, +1 for the row's.
            if (table.pos + 1 + rowOffset + 1 + cellOffset === cell.pos) found = column;
            column += colspan;
        });
    });

    return found;
}

/** How many columns the table has, on the grid. */
export function columnCount(editor: any): number {
    const table = tableAt(editor);
    const first = table?.node.firstChild;
    if (!first) return 0;
    let columns = 0;
    first.forEach((cell: any) => { columns += cell.attrs?.colspan || 1; });
    return columns;
}

/** The types declared on the caret's table, padded to its width. */
export function tableColumnTypes(editor: any): ColumnType[] {
    const table = tableAt(editor);
    if (!table) return [];
    return padded(declaredTypes(table.node), columnCount(editor));
}

/** The type on the column the caret is in. */
export function currentColumnType(editor: any): ColumnType {
    const index = columnIndex(editor);
    if (index === null) return null;
    return tableColumnTypes(editor)[index] ?? null;
}

/** Set (or clear) the type of the column the caret is in. */
export function setCurrentColumnType(editor: any, type: ColumnType) {
    const table = tableAt(editor);
    const index = columnIndex(editor);
    if (!editor || !table || index === null) return;
    const next = withTypeAt(declaredTypes(table.node), index, type);
    const { state, view } = editor;
    view.dispatch(state.tr.setNodeMarkup(table.pos, undefined, {
        ...table.node.attrs, columnTypes: next.length ? next : null,
    }));
    view.focus();
}


/**
 * The cells that break their column's type, marked where they are.
 *
 * SHOWN WHILE SOMEBODY TYPES, not at submission. The server is what decides -- t11 counts these on
 * save and t1 refuses to submit while the count is non-zero -- but being told at submission that a
 * cell three pages back is wrong, with no way to see which, is a refusal nobody can act on. So the
 * rule runs here too, over the document already in memory, and points at the cell.
 *
 * A DECORATION, NOT AN ATTRIBUTE. Nothing about being wrong belongs in the document: it is derived
 * from the text and the column's type, it changes as the text does, and writing it into the body
 * would put it in the digest people sign.
 */
export const ColumnTypeMarks = Extension.create({
    name: "columnTypeMarks",

    addProseMirrorPlugins() {
        return [new Plugin({
            key: new PluginKey("parkourColumnTypes"),
            props: {
                decorations(state) {
                    const found: Decoration[] = [];

                    state.doc.descendants((table, tablePos) => {
                        if (table.type.name !== "table") return true;
                        const types = declaredTypes(table);
                        if (!types.some(Boolean)) return false;

                        const occupied = new Set<string>();
                        let row = -1;

                        table.forEach((rowNode: any, rowOffset: number) => {
                            row += 1;
                            let column = 0;
                            rowNode.forEach((cell: any, cellOffset: number) => {
                                while (occupied.has(`${row}:${column}`)) column += 1;
                                const colspan = cell.attrs?.colspan || 1;
                                const rowspan = cell.attrs?.rowspan || 1;
                                for (let dr = 0; dr < rowspan; dr += 1) {
                                    for (let dc = 0; dc < colspan; dc += 1) {
                                        occupied.add(`${row + dr}:${column + dc}`);
                                    }
                                }

                                const type = types[column];
                                // A HEADER HOLDS THE LABEL, NOT A VALUE, and a merged cell is in no
                                // one column -- the same two exclusions the server makes.
                                if (type && colspan === 1 && cell.type.name === "tableCell") {
                                    const text = cell.textBetween(0, cell.content.size, " ")
                                                     .replace(/\s+/g, " ").trim();
                                    const problem = violation(type, text);
                                    if (problem) {
                                        const at = tablePos + 1 + rowOffset + 1 + cellOffset;
                                        found.push(Decoration.node(at, at + cell.nodeSize, {
                                            class: "parkour-cell-invalid",
                                            title: `“${text}” ${problem}`,
                                        }));
                                    }
                                }
                                column += colspan;
                            });
                        });
                        return false;
                    });

                    return DecorationSet.create(state.doc, found);
                },
            },
        })];
    },
});


/**
 * Re-ordering a table's rows by one column.
 *
 * WHY THIS WAITED, and it was never the sorting. Re-ordering rows is an edit to text people sign:
 * in a diff it reads as a hundred unrelated changes, because every cell in the table moved. So the
 * work was making the trail able to say what actually happened -- the save that follows a sort
 * carries Source "SORT", the audit row reads SORTED rather than SAVED, and the server keeps a
 * snapshot of the table as it stood before. Anybody asking "who re-ordered this and when" gets one
 * answer instead of a wall of moved text.
 *
 * HEADER ROWS DO NOT MOVE. A header is the column's label; sorting it into the middle of the data
 * is never what anybody meant.
 *
 * MERGED ROWS REFUSE THE SORT. A row that spans another cannot be moved without taking its
 * neighbour with it, and quietly re-ordering half of a merge would corrupt the table. Refused with
 * a reason rather than attempted.
 */
/** The text of a row's cell in `column`, for comparing. */
function cellTextAt(row: any, column: number): string {
    let at = 0;
    let found = "";
    row.forEach((cell: any) => {
        const colspan = cell.attrs?.colspan || 1;
        if (at === column) found = cell.textBetween(0, cell.content.size, " ");
        at += colspan;
    });
    return found.replace(/\s+/g, " ").trim();
}

/** Can the caret's table be sorted, and if not, why not? */
export function sortableReason(editor: any): string | null {
    const table = tableAt(editor);
    if (!table) return TABLE_REASON_TEXT;
    if (columnIndex(editor) === null) return "Put the caret in the column to sort by.";

    let merged = false;
    let bodyRows = 0;
    table.node.forEach((row: any) => {
        let isHeader = false;
        row.forEach((cell: any) => {
            if (cell.type.name === "tableHeader") isHeader = true;
            if ((cell.attrs?.rowspan || 1) > 1) merged = true;
        });
        if (!isHeader) bodyRows += 1;
    });

    if (merged) {
        return "This table has cells merged across rows, and moving one would take its neighbour "
             + "with it. Unmerge them to sort.";
    }
    if (bodyRows < 2) return "There is nothing to sort: the table has one row of data.";
    return null;
}

const TABLE_REASON_TEXT = "Put the caret in a table first.";

/** Sort the caret's table by the caret's column. Returns true if the document changed. */
export function sortRows(editor: any, direction: SortDirection): boolean {
    const table = tableAt(editor);
    const column = columnIndex(editor);
    if (!editor || !table || column === null || sortableReason(editor)) return false;

    // HEADERS KEEP THEIR PLACE; only the rows below them are ordered.
    const header: any[] = [];
    const body: any[] = [];
    table.node.forEach((row: any) => {
        let isHeader = false;
        row.forEach((cell: any) => { if (cell.type.name === "tableHeader") isHeader = true; });
        (isHeader ? header : body).push(row);
    });
    if (body.length < 2) return false;

    // DECORATED, SORTED, UNDECORATED -- so the comparison runs once per row rather than once per
    // comparison, and so a STABLE sort is guaranteed: rows that compare equal keep the order the
    // author put them in, which in a procedure is usually meaningful.
    const ordered = sortedByKey(body, row => cellTextAt(row, column), direction);

    if (ordered.every((row, at) => row === body[at])) return false;   // already in order

    const { state, view } = editor;
    const schema = state.schema;
    const next = schema.nodes.table.create(table.node.attrs, [...header, ...ordered]);
    view.dispatch(state.tr.replaceWith(table.pos, table.pos + table.node.nodeSize, next));
    view.focus();
    return true;
}
